/* eslint-disable no-console -- Dev-only hook that patches and restores console.error for rrweb capture */
/**
 * useDevRecorder — rrweb session recording for the dev toolbar.
 *
 * Dynamically imports rrweb only when recording starts (180KB, code-split).
 * Captures DOM snapshots and console.error as custom rrweb events.
 * Network events are captured by trace-recorder (in the export `trace` field).
 * Dev-only — all callbacks no-op in production builds.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import type { eventWithTime } from '@rrweb/types';
import { startFresh, stopTraceRecording, getTraceEvents, clearTrace } from '../lib/trace-recorder';

type RecorderState = 'idle' | 'recording' | 'recorded';

export interface DevRecorder {
  state: RecorderState;
  /** Seconds elapsed since recording started (updates every second). */
  elapsed: number;
  start: () => void | Promise<unknown>;
  stop: () => void;
  exportRecording: () => void;
  /** The recorded events — only populated when state === 'recorded'. */
  events: eventWithTime[];
  reset: () => void;
}

/** Noop constant for non-dev — avoids allocating new objects each render. */
const EMPTY_EVENTS: eventWithTime[] = [];
const noop = () => {};

export function useDevRecorder(): DevRecorder {
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [events, setEvents] = useState<eventWithTime[]>(EMPTY_EVENTS);

  const eventsRef = useRef<eventWithTime[]>([]);
  const stopRecorderRef = useRef<(() => void) | null>(null);
  const startTimeRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cleanupConsoleRef = useRef<(() => void) | null>(null);
  const recordingActiveRef = useRef(false);

  // Tick elapsed every second while recording
  useEffect(() => {
    if (state !== 'recording') return;
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [state]);

  const start = useCallback(async () => {
    if (!import.meta.env.DEV) return;

    eventsRef.current = [];
    startTimeRef.current = Date.now();
    setElapsed(0);
    setState('recording');

    // Start trace-recorder alongside rrweb — captures semantic events
    // (clicks with testids, API calls, navigations) for story generation
    startFresh();

    // Dynamic import — only loads rrweb when the user clicks Record
    const rrweb = await import('rrweb');

    const stopFn = rrweb.record({
      emit: (event: eventWithTime) => {
        eventsRef.current.push(event);
      },
    });
    stopRecorderRef.current = stopFn ?? null;
    recordingActiveRef.current = true;

    // Safe wrapper — only emits custom events while recording is active
    const safeAddCustomEvent = (tag: string, payload: Record<string, unknown>) => {
      if (recordingActiveRef.current) {
        try { rrweb.record.addCustomEvent(tag, payload); } catch { /* recorder already stopped */ }
      }
    };

    // Network events are captured by trace-recorder (included in export `trace` field).
    // rrweb replay still works at the DOM level without network custom events.

    // --- Console.error capture ---
    const origConsoleError = console.error;
    console.error = (...errorArgs: unknown[]) => {
      safeAddCustomEvent('console-error', {
        message: errorArgs.map(String).join(' '),
      });
      origConsoleError.apply(console, errorArgs);
    };
    cleanupConsoleRef.current = () => {
      console.error = origConsoleError;
    };
  }, []);

  const stop = useCallback(() => {
    recordingActiveRef.current = false;
    stopRecorderRef.current?.();
    stopRecorderRef.current = null;
    stopTraceRecording();
    cleanupConsoleRef.current?.();
    cleanupConsoleRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;

    const captured = [...eventsRef.current];
    setEvents(captured);
    setState('recorded');
  }, []);

  const exportRecording = useCallback(() => {
    const payload = {
      session: {
        name: `terra-recording-${new Date().toISOString().replace(/[:.]/g, '-')}`,
        timestamp: new Date().toISOString(),
      },
      events: eventsRef.current,
      trace: getTraceEvents(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${payload.session.name}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const reset = useCallback(() => {
    eventsRef.current = [];
    setEvents(EMPTY_EVENTS);
    clearTrace();
    setElapsed(0);
    setState('idle');
  }, []);

  // In production builds, Vite dead-code-eliminates the hook body above.
  // Return no-ops so the component tree is stable regardless.
  if (!import.meta.env.DEV) {
    return { state: 'idle', elapsed: 0, start: noop, stop: noop, exportRecording: noop, events: EMPTY_EVENTS, reset: noop };
  }

  return { state, elapsed, start, stop, exportRecording, events, reset };
}
